import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Paper,
  TextInput,
  Text,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Center,
  ActionIcon,
} from "@mantine/core";
import { IconMinus, IconX } from "@tabler/icons-react";

import useColors from "../../../theme/useColors";
import { allowedSignaturePattern } from "../../../constants/app-constant";
import {
  renderSignatureCanvas,
  SIGNATURE_FONT_FAMILY,
} from "../../../utils/signature-utils";

interface SignatureInputDialogProps {
  initialValue?: string;
  onSubmit?: (signature: string, file: File) => void;
  onCancel?: () => void;
  opened: boolean;
  minLength?: number;
  maxLength?: number;
}

const SignatureInputDialog: React.FC<SignatureInputDialogProps> = ({
  initialValue = "",
  onSubmit,
  onCancel,
  opened,
  minLength = 2,
  maxLength = 35,
}) => {
  const [signature, setSignature] = useState(initialValue);
  const [fontSize, setFontSize] = useState(3);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const signatureTouchedRef = useRef(false);
  const prevOpenedRef = useRef(false);
  const colors = useColors();

  const resetAfterClose = useCallback(() => {
    setTimeout(() => {
      setSignature(initialValue);
    }, 1000);
  }, [initialValue]);

  const handleClose = useCallback(() => {
    onCancel?.();
    resetAfterClose();
  }, [onCancel, resetAfterClose]);

  const handleSubmit = async () => {
    if (signature.length < minLength) {
      setError(`Signature must be at least ${minLength} characters`);
      return;
    }

    if (signature.length > maxLength) {
      setError(`Signature cannot exceed ${maxLength} characters`);
      return;
    }

    try {
      await waitForFontResize();
      const canvas = await renderSignatureCanvas(signature, fontSize);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "signature.png", { type: "image/png" });
          onSubmit?.(signature, file);
        } else {
          console.error("Could not convert canvas to blob.");
        }
      }, "image/png");
    } catch (err) {
      console.error("Error generating signature image:", err);
    } finally {
      resetAfterClose();
    }
  };

  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    signatureTouchedRef.current = true;
    setSignature(value);
    setError(null);

    if (!allowedSignaturePattern.test(value)) {
      setError(
        "Signature must only contain letters, spaces, underscores, or dashes (no numbers or special characters)"
      );
      return;
    }

    if (value.length > 0 && value.length < minLength) {
      setError(`Signature must be at least ${minLength} characters`);
    } else if (value.length > maxLength) {
      setError(`Signature cannot exceed ${maxLength} characters`);
    }
  };

  useEffect(() => {
    const justOpened = opened && !prevOpenedRef.current;
    prevOpenedRef.current = opened;

    if (!justOpened) return;

    signatureTouchedRef.current = false;
    setSignature(initialValue);
    setError(null);
  }, [opened, initialValue]);

  useEffect(() => {
    if (!opened || signatureTouchedRef.current || !initialValue) return;
    setSignature(initialValue);
  }, [initialValue, opened]);

  const adjustFontSize = () => {
    if (!textRef.current || !containerRef.current) return;

    setFontSize(5);

    requestAnimationFrame(() => {
      if (!textRef.current || !containerRef.current) return;

      const textWidth = textRef.current.scrollWidth;
      const textHeight = textRef.current.scrollHeight;
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      if (textWidth === 0 || textHeight === 0) return;

      const widthRatio = (containerWidth * 0.85) / textWidth;
      const heightRatio = (containerHeight * 0.85) / textHeight;
      const ratio = Math.min(widthRatio, heightRatio);

      setFontSize(Math.max(1, Math.min(5, 5 * ratio)));
    });
  };

  const waitForFontResize = () =>
    new Promise<void>((resolve) => {
      adjustFontSize();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => adjustFontSize());
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [signature]);

  useEffect(() => {
    if (opened) {
      setTimeout(() => adjustFontSize(), 50);
    }
  }, [opened]);

  const isValidSignature =
    allowedSignaturePattern.test(signature) &&
    signature.trim().length >= minLength &&
    signature.trim().length <= maxLength;

  return (
    <Modal
      withCloseButton={false}
      opened={opened}
      onClose={handleClose}
      centered
      size='md'
      padding='0rem'
    >
      <Group
        p={"12px"}
        style={{ borderRadius: "6px" }}
        justify='space-between'
        gap='xs'
        bg={colors.gray[2]}
      >
        <Box>Please sign the document</Box>
        <Group justify='flex-end' gap='xs'>
          <ActionIcon
            variant='subtle'
            color='gray'
            aria-label='Minimize'
            size='md'
            onClick={handleClose}
          >
            <IconMinus size={16} stroke={1.5} />
          </ActionIcon>

          <ActionIcon
            variant='subtle'
            color='gray'
            aria-label='Close'
            size='md'
            className='hover:bg-red-500 hover:text-white'
            onClick={handleClose}
          >
            <IconX size={16} stroke={1.5} />
          </ActionIcon>
        </Group>
      </Group>

      <Stack p={"sm"}>
        <TextInput
          placeholder='Your signature'
          value={signature}
          onChange={handleSignatureChange}
          maxLength={maxLength}
          error={error}
          size='md'
          styles={{
            input: {
              fontSize: "16px",
              fontFamily: "Roboto, sans-serif",
            },
          }}
        />

        <Paper
          p='sm'
          withBorder={false}
          style={{
            minHeight: "120px",
            width: "100%",
            maxWidth: "568px",
            margin: "0 auto",
            backgroundColor: "#F5F5F5",
            overflow: "visible",
            position: "relative",
            boxSizing: "border-box",
          }}
          ref={containerRef}
        >
          <Center style={{ minHeight: "120px", padding: "12px 16px" }}>
            {signature ? (
              <div
                ref={textRef}
                style={{
                  fontFamily: SIGNATURE_FONT_FAMILY,
                  fontSize: `${fontSize}rem`,
                  fontWeight: 500,
                  color: "#000",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                }}
              >
                {signature}
              </div>
            ) : (
              <Text c='gray.5' ta='center'>
                Your preview here
              </Text>
            )}
          </Center>
        </Paper>
        <Box
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <Button variant='outline' onClick={handleClose}>
            <Text size='14px' fw={600} c={colors.gray[8]}>
              Cancel
            </Text>
          </Button>
          <Button onClick={handleSubmit} disabled={!isValidSignature}>
            Submit
          </Button>
        </Box>
      </Stack>
    </Modal>
  );
};

export default SignatureInputDialog;
